// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for retrieving fixed assets snapshot.
 *
 * Provides functionality to fetch fixed assets snapshot based on a specific date.
 * Each response covers a single cost currency: the backend-computed summary stats
 * for that currency plus the assets denominated in it.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import {
  IsoCurrencyCode,
  isoCurrencyCodeSchema,
} from '@/currency/constants/iso';
import {
  FixedAssetSnapshotItem,
  fixedAssetSnapshotItemSchema,
} from '@/report/api/types';
import { parseArrayInChunks } from '@/utilities/chunked-parse';
import { DateTime } from 'luxon';
import { z } from 'zod';

/** Backend-computed summary stats for a single cost currency's fixed-asset snapshot. */
export const fixedAssetCurrencyStatsSchema = z.object({
  isoCode: isoCurrencyCodeSchema,
  assetCount: z.number(),
  assetsWithCompleteAccounting: z.number(),
  assetsWithIncompleteAccounting: z.number(),
  weightedInServiceDays: z.number().nullable(),
  weightedUsefulLifeRemaining: z.number().nullable(),
  totalDepreciableAmount: z.string().nullable(),
  totalAccumulatedDepreciation: z.string().nullable(),
  totalBookValue: z.string().nullable(),
});

export type FixedAssetCurrencyStats = z.infer<
  typeof fixedAssetCurrencyStatsSchema
>;

// The stats live under the response envelope's data field; assets are stripped here
// and validated separately in chunks below.
const fixedSnapshotStatsResponseSchema = z.object({
  data: fixedAssetCurrencyStatsSchema,
  metadata: responseMetadataSchema,
});

/** A single currency's fixed-asset snapshot: backend stats plus its assets. */
export interface FixedAssetsSnapshot extends FixedAssetCurrencyStats {
  assets: FixedAssetSnapshotItem[];
}

// The raw wire response keeps assets as an unparsed array so they can be validated
// in chunks (snapshots can be large); the stats are validated up front.
interface RawFixedAssetsSnapshotResponse {
  data: FixedAssetCurrencyStats & {
    assets: FixedAssetSnapshotItem[];
  };
}

interface FixedAssetsSnapshotParameters {
  asOfDate: DateTime;
  currency: IsoCurrencyCode;
}

/**
 * Retrieves a snapshot of fixed assets as of a specific date for a single cost
 * currency.
 *
 * @param params - The parameters for the snapshot request
 * @returns A promise resolving to the single-currency fixed-asset snapshot
 */
export async function getFixedAssetsSnapshot(
  params: FixedAssetsSnapshotParameters
): Promise<FixedAssetsSnapshot> {
  const asOfDateString = params.asOfDate.toISODate();

  const path = '/v1/report/asset/fixed-snapshot';
  const response = await api.get<RawFixedAssetsSnapshotResponse>(path, {
    params: {
      asOfDate: asOfDateString,
      currency: params.currency,
    },
  });

  const parsed = await parseResponse(
    fixedSnapshotStatsResponseSchema,
    response.data,
    { schemaName: 'FixedAssetsSnapshotStats', endpoint: path }
  );
  const stats = parsed.data;
  const assets = await parseArrayInChunks(
    response.data.data.assets,
    fixedAssetSnapshotItemSchema,
    {
      schemaValidation: {
        schemaName: 'FixedAssetSnapshotItem',
        endpoint: path,
        extra: {
          asOfDate: asOfDateString,
          isoCode: stats.isoCode,
        },
      },
    }
  );
  return { ...stats, assets };
}
